<template>
    <div class="card-container">
        <BaseCard class="flex gap-2.5 h-full card">
            <div :id="questionId" class="text-ink-muted body-small-semibold">{{ t("moments.managerSelection.question", { momentTitle }) }}</div>
            <div class="flex gap-5 h-full flex-1 justify-end card-button-container">
                <BaseRadioGroup v-model="selectedValue" :options="reviewOptions" orientation="horizontal" :aria-labelledby="questionId" />
                <BaseButton theme="secondary" :disabled="!selectedValue" @click="handleConfirm"> {{ t("moments.managerSelection.confirm") }} </BaseButton>
            </div>
        </BaseCard>
        <BaseToast v-model:open="isErrorToastOpen" theme="error" :description="errorMessage" />
    </div>
</template>

<script setup lang="ts">
import { router } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseToast from "~vue/components/BaseToast.vue";
import BaseRadioGroup from "~vue/components/form/BaseRadioGroup.vue";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, onMounted, ref, useId } from "vue";
import { useI18n } from "vue-i18n";

import { PERFORMANCE_REVIEW_EVENT_PREFIX } from "./constants";

const props = withDefaults(
    defineProps<{
        momentId?: string | number | null;
        momentTitle?: string | null;
    }>(),
    { momentId: null, momentTitle: "" },
);

const { t } = useI18n();

const questionId = useId();
const selectedValue = ref<string>();
const reviewOptions = computed(() => [
    { value: "yes", label: t("moments.managerSelection.yes") },
    { value: "no", label: t("moments.managerSelection.no") },
]);

const { execute: sendReviewSelection, error: sendReviewSelectionError } = useFetch("/api/coach/performance_reviews/user_data/", { immediate: false })
    .post(
        computed(() => ({
            has_people_to_review: selectedValue.value === "yes",
        })),
    )
    .json();

const isErrorToastOpen = ref(false);
const errorMessage = ref("");

const handleConfirm = async () => {
    // execute() resolves even on HTTP errors, exposing them via the error ref instead of throwing.
    await sendReviewSelection();
    if (sendReviewSelectionError.value) {
        errorMessage.value = t("moments.managerSelection.submitError");
        isErrorToastOpen.value = true;
        return;
    }

    logUserInteraction(`${PERFORMANCE_REVIEW_EVENT_PREFIX}has_people_to_review_selected`, {
        has_people_to_review: selectedValue.value === "yes",
        moment_id: props.momentId,
    });
    router.reload({ only: ["has_people_to_review"] });
};

onMounted(() => logUserInteraction(`${PERFORMANCE_REVIEW_EVENT_PREFIX}has_people_to_review_question_presented`));
</script>

<style scoped>
.card-container {
    container-type: inline-size;
    width: 100%;
}

.card,
.card-button-container {
    flex-direction: column;
    align-items: flex-start;
}
@container (min-width: 600px) {
    .card,
    .card-button-container {
        align-items: center;
        flex-direction: row;
    }
}
</style>
